import * as XLSX from "xlsx";
import { ReconResult, DbRow, CsvRow, XlsRow } from "../../features/recon/ReconSlice";

// ── Summary sheet ─────────────────────────────────────────────────────────────

function buildSummarySheet(result: ReconResult): XLSX.WorkSheet {
  const { summary } = result;
  const isSelp = summary.processorType === "SELP";
  const fileRowLabel = isSelp ? "Total CSV Rows" : "Total XLS Rows";
  const fileRowCount = isSelp ? (summary.totalCsvRows ?? 0) : (summary.totalXlsRows ?? 0);

  const rows: (string | number | null)[][] = [
    ["Recon Summary"],
    [],
    ["Processor",       summary.processorType],
    ["File Name",       summary.fileName],
    ["Query Date From", summary.dateFrom],
    ["Query Date To",   summary.dateTo],
    ["File Date From",  summary.fileDateFrom ?? "-"],
    ["File Date To",    summary.fileDateTo   ?? "-"],
    [],
    [fileRowLabel,           fileRowCount],
    ["Total DB Rows",        summary.totalDbRows],
    ["Matched (Clean)",      summary.matchedCount],
    ["Total Discrepancies",  summary.discrepancyCount],
    [],
    ["Discrepancy Breakdown"],
    ["Type", "Count"],
    ...Object.entries(summary.byType).map(([type, count]) => [type, count]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 28 }, { wch: 40 }];
  return ws;
}

// ── Discrepancies sheet ───────────────────────────────────────────────────────

function buildDiscrepanciesSheet(result: ReconResult): XLSX.WorkSheet {
  const { discrepancies, summary } = result;
  const isSelp = summary.processorType === "SELP";

  const headers = isSelp
    ? ["Discrepancy Type", "Voucher Code", "Saral ID", "CSV Row #", "CSV Value", "DB Value", "State", "Program", "Principal", "Distributor"]
    : ["Discrepancy Type", "Voucher Code (UMN)", "Saral ID", "XLS Row #", "XLS Value", "DB Value", "State", "Program", "Principal", "Distributor"];

  const rows = discrepancies.map((d) => [
    d.discrepancyType,
    d.voucherCode ?? "-",
    d.saralId     ?? "-",
    (isSelp ? d.csvRowNumber : d.xlsRowNumber) ?? "-",
    d.csvValue    ?? "-",
    d.dbValue     ?? "-",
    d.dbRow?.state           ?? "-",
    d.dbRow?.programName     ?? "-",
    d.dbRow?.principalName   ?? "-",
    d.dbRow?.distributorName ?? "-",
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 20) }));
  return ws;
}

// ── Processor Data sheet (all file rows as-is) ────────────────────────────────

function buildProcessorDataSheet(result: ReconResult): XLSX.WorkSheet {
  const { processorRows, summary } = result;
  const isSelp = summary.processorType === "SELP";

  if (isSelp) {
    const csvRows = processorRows as CsvRow[];
    const headers = ["Code", "Credits", "Currency (Devise)", "Reference", "Date Distribution"];
    const rows = csvRows.map((r) => [r.code, r.credits, r.devise, r.reference, r.dateDistribution ?? "-"]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    return ws;
  } else {
    const xlsRows = processorRows as XlsRow[];
    const headers = ["UMN (Voucher Code)", "UUID (NPCI)", "Phone", "Amount", "Balance", "Issue Date", "Expiry Date"];
    const rows = xlsRows.map((r) => [r.umn, r.uuid ?? "-", r.phone ?? "-", r.amount ?? "-", r.balance ?? "-", r.issueDate ?? "-", r.expiryDate ?? "-"]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    return ws;
  }
}

// ── System Data sheet (all DB vouchers) ──────────────────────────────────────

function buildSystemDataSheet(result: ReconResult): XLSX.WorkSheet {
  const { dbRows, summary } = result;
  const isSelp = summary.processorType === "SELP";

  if (isSelp) {
    const headers = [
      "Saral ID", "Voucher Code", "State", "Amount", "Currency",
      "Voucher Ref", "Created At (UTC)", "Program", "Principal", "Distributor",
    ];
    const rows = (dbRows as DbRow[]).map((r) => [
      r.saralId        ?? "-",
      r.code           ?? "-",
      r.state          ?? "-",
      r.amount         ?? "-",
      r.currency       ?? "-",
      r.voucherRef     ?? "-",
      r.createdAtUtc   ?? "-",
      r.programName    ?? "-",
      r.principalName  ?? "-",
      r.distributorName ?? "-",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    return ws;
  } else {
    const headers = [
      "Saral ID", "UMN (Code)", "State", "UUID (NPCI)", "Amount", "Available Amount",
      "Phone", "Issue Date (IST)", "Expiry Date (IST)", "Program", "Principal", "Distributor",
    ];
    const rows = (dbRows as DbRow[]).map((r) => [
      r.saralId          ?? "-",
      r.code             ?? "-",
      r.state            ?? "-",
      r.voucherUuid      ?? "-",
      r.amount           ?? "-",
      r.availableAmount  ?? "-",
      r.beneficiaryPhone ?? "-",
      r.voucherCreateDate ?? "-",
      r.expiryDate       ?? "-",
      r.programName      ?? "-",
      r.principalName    ?? "-",
      r.distributorName  ?? "-",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    return ws;
  }
}

// ── Main export ───────────────────────────────────────────────────────────────

export function downloadReconReport(result: ReconResult): void {
  const { summary } = result;
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildSummarySheet(result),          "Summary");
  XLSX.utils.book_append_sheet(wb, buildDiscrepanciesSheet(result),    "Discrepancies");
  XLSX.utils.book_append_sheet(wb, buildProcessorDataSheet(result),    "Processor Data");
  XLSX.utils.book_append_sheet(wb, buildSystemDataSheet(result),       "System Data");

  const fileName = `${summary.processorType}_Recon_${summary.dateFrom}.xlsx`;
  XLSX.writeFile(wb, fileName);
}
